import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  SegmentedControl,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  DENIAL_HINTS,
  hasIvtAdminAccess,
  hasIvtEditorAccess,
  type DenialKind,
} from '../../utils/authorization';
import { useIndustryFlow } from './useIndustryFlow';
import { useAssetFlow } from './useAssetFlow';
import { useAllowedIndustries } from './useAllowedIndustries';
import { KpiBand } from './components/KpiBand';
import { IndustryTable } from './components/IndustryTable';
import { AssetTable } from './components/AssetTable';
import { formatArr } from './components/scale';
import { AccountPipelineList } from './components/AccountPipelineList';
import { CoverageCard } from './components/CoverageCard';
import { useAccountPipeline } from './useAccountPipeline';
import { useAccountPipelineFilterOptions, useHierarchyNodeOptions } from './useAccountPipelineFilterOptions';
import { defaultAccountPipelineQuery, queryToAccountPipelineFilterParam } from './accountPipelineFilter';
import { useDismissal } from './useDismissal';
import { useMotionClaim, type MotionBlock } from './useMotionClaim';
import { AddMotionModal } from './components/AddMotionModal';
import { RadarSection } from './components/RadarSection';
import { RadarDirectionModal } from './components/RadarDirectionModal';
import { useRadarDirections } from './useRadarDirections';
import { useRadarDirection } from './useRadarDirection';
import { DEFAULT_MIN_SCORE, visibleSuggestions } from './components/AccountPipelineItem';
import type { LighthouseNotice } from './useLighthouseEdits';
import type { AccountPipelineGroup, AccountPipelineOpportunity } from '@amzn/ivthub-client';
import { WindowSelect } from './components/WindowSelect';
import { IndustrySelect } from './components/IndustrySelect';
import { DEFAULT_PRESET, effectiveWindow, type WindowPresetId } from './components/windowPresets';
import { AccountFlag } from '@amzn/ivthub-client';
import { canWriteFlag, useAccountFlag } from './useAccountFlag';
import { useLighthouseEdits } from './useLighthouseEdits';
import { AddLighthouseAccountModal } from './components/AddLighthouseAccountModal';
import {
  DeleteLighthouseAccountModal,
  type LighthouseDeleteTarget,
} from './components/DeleteLighthouseAccountModal';
import './DashboardPage.css';

type DashboardView = 'gm' | 'sto';

/** Per-view copy for the two load states. Everything else about them is shared. */
const VIEW_COPY: Record<DashboardView, { loading: string; errorHeader: string }> = {
  gm: { loading: '加载行业看板…', errorHeader: '无法加载行业看板' },
  sto: { loading: '加载资产看板…', errorHeader: '无法加载资产看板' },
};

/**
 * IVT Leader Home — two views behind one header.
 *
 * Figma: page `3984:1108`, frame `4019:45173` (GM) and `4146:50140` (STO).
 *
 * | view | op | tier | DAT |
 * |---|---|---|---|
 * | GM | `GetIndustryPipelineFlow` | ADMIN_ONLY | 8997 |
 * | STO & IS/ISA | `GetAssetPipelineFlow` | ANY_MEMBER | 9005 |
 *
 * One backend call per window per view supplies that view's KPI band; this file owns
 * the window state, the selected view and the load states, and the display parts land
 * on top of it. The window selector and the view switch are SHARED — only the content
 * area swaps — so both hooks stay mounted and the inactive one is told not to fetch.
 */
/**
 * The outcome of the last write above a block — the dialogs close on Confirm and say nothing
 * themselves. Alerts rather than a Flashbar: see useLighthouseEdits for the scroll-to-top a
 * Flashbar's dismiss caused.
 */
function renderNotices(notices: LighthouseNotice[]) {
  if (notices.length === 0) return null;
  return (
    <SpaceBetween size="xs">
      {notices.map((n) => (
        <Alert key={n.id} type={n.type} header={n.header} dismissible={n.dismissible} onDismiss={n.onDismiss}>
          {n.loading ? (
            <span className="ivt-dashboard__refreshing">
              <Spinner size="normal" />
              {n.content}
            </span>
          ) : (
            n.content
          )}
        </Alert>
      ))}
    </SpaceBetween>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const openRadarPreferences = useCallback(() => navigate('/ivt/radar-config'), [navigate]);
  const { given_name, display_name, sub, ldap_groups } = useAuth();
  /**
   * Which view to LAND on. The GM view is ADMIN_ONLY, so without this a non-admin's
   * first paint is the denial panel below, and the STO view they are entitled to sits
   * one unexplained click away.
   *
   * ⚠️ The only place this page reads `ldap_groups`, and it reads them to choose a
   * starting view — never to decide whether a view may be opened. That stays the
   * service's call (see `noAccess`), so a non-admin who picks GM anyway still gets the
   * denial panel from the 403 rather than from a client-side guess.
   *
   * Safe to resolve once, at mount: this page renders inside IvtLayout, which shows its
   * own non-member panel until `ldap_groups` is populated.
   */
  const [view, setView] = useState<DashboardView>(() =>
    hasIvtAdminAccess(ldap_groups) ? 'gm' : 'sto',
  );
  /**
   * The clock the presets are relative to. Re-read on explicit user actions only —
   * opening the dropdown, choosing a preset, retrying — and never on a passive
   * re-render, so the window a leader is reading never shifts underneath them.
   *
   * It cannot be frozen at mount either: a dashboard tab left open past a month or
   * quarter boundary would keep labelling the old month 本月 and would go on hiding
   * the new quarter entirely (availablePresets drops a quarter that has not started
   * relative to this anchor), leaving the current quarter unselectable.
   */
  const [anchor, setAnchor] = useState(() => new Date());
  const [presetId, setPresetId] = useState<WindowPresetId>(DEFAULT_PRESET);
  /**
   * The STO view's industry scope — request scope like the window, so it lives here and a
   * change to it refetches. Exactly ONE industry: the STO view does not query "all
   * industries" (a product decision), so there is no value here that means all of them.
   *
   * What the user PICKED, not what is queried — see `industry` below. `null` until they pick.
   *
   * Kept across a view switch even though the GM view has no selector: that view renders a
   * per-industry matrix and has no use for one, and discarding the selection would silently
   * move the STO view back to the default industry on the way back.
   */
  const [pickedIndustry, setPickedIndustry] = useState<string | null>(null);
  /** The caller's permitted industries, fetched only once the STO view is opened. */
  const allowed = useAllowedIndustries(sub, view === 'sto');
  /**
   * The industry the STO requests actually carry: the pick if it is still permitted, else the
   * FIRST permitted industry, else `null`.
   *
   * Derived during render rather than written back into state by an effect, so there is no
   * render in which the list has arrived but the scope is still unset — an effect would let the
   * three STO hooks see `null` for one more commit.
   *
   * `null` means nothing can be queried yet (list loading, failed, or empty), and the hooks send
   * nothing for it — which is what stops an "all industries" request going out while the list
   * loads. The loading/failed/empty cases are told apart below via `allowed.status`.
   */
  const permittedIndustries = allowed.industries;
  const industry =
    permittedIndustries === null
      ? null
      : pickedIndustry !== null && permittedIndustries.includes(pickedIndustry)
        ? pickedIndustry
        : (permittedIndustries[0] ?? null);
  /** Request scope like the window, one per block: `null` leaves the server's default of 3. */
  const [lighthouseScore, setLighthouseScore] = useState<number | null>(null);
  const [reclaimScore, setReclaimScore] = useState<number | null>(null);
  /**
   * Each block's property filter (DAT-9223), request scope like the score. Both start at PM's
   * default — Launched / Completed and Closed Lost hidden — which is SENT, because an omitted
   * filter means none. Kept across window and industry changes, as the score is.
   */
  const [lighthouseQuery, setLighthouseQuery] = useState(defaultAccountPipelineQuery);
  const [reclaimQuery, setReclaimQuery] = useState(defaultAccountPipelineQuery);
  const lighthouseFilter = useMemo(() => queryToAccountPipelineFilterParam(lighthouseQuery), [lighthouseQuery]);
  const reclaimFilter = useMemo(() => queryToAccountPipelineFilterParam(reclaimQuery), [reclaimQuery]);

  // One derivation for both the request and the label — see effectiveWindow for why
  // deriving the fallback twice is the bug this avoids.
  const { id: effectivePresetId, range } = effectiveWindow(presetId, anchor);

  // Exactly one of the two fetches is live, keyed off the switch. Both hooks are mounted
  // because hooks cannot be conditional; `enabled` is what stops the inactive view
  // issuing a request — in GM's case a guaranteed 403 for every non-admin.
  // Coming back to a view refetches it: `enabled` is just another effect dep, and caching
  // per window would need bookkeeping this page does not have. It never blanks, because
  // each hook keeps its own last response and the refetch indicator covers the gap.
  const gmFlow = useIndustryFlow(range, view === 'gm');
  const stoFlow = useAssetFlow(range, industry, view === 'sto');
  // The two blocks are one operation with `lighthouse` flipped; the page shows both at once.
  const lighthouseAccounts = useAccountPipeline(range, industry, true, lighthouseScore, view === 'sto', lighthouseFilter);
  const reclaimAccounts = useAccountPipeline(range, industry, false, reclaimScore, view === 'sto', reclaimFilter);
  // Candidates per block — each block's names are its own — over one shared hierarchy catalog.
  const hierarchyNodes = useHierarchyNodeOptions(view === 'sto');
  const lighthouseOptions = useAccountPipelineFilterOptions(range, industry, true, lighthouseScore, hierarchyNodes, view === 'sto');
  const reclaimOptions = useAccountPipelineFilterOptions(range, industry, false, reclaimScore, hierarchyNodes, view === 'sto');
  // Radar section (DAT-9115, R17). Scoped to the STO industry — one request
  // per industry, CANDIDATE only; the hook keeps its own last response like
  // the asset-flow and account-pipeline hooks above. The detail modal and
  // the preferences page are R18 / R16 and are passed as no-op placeholders.
  const radarDirections = useRadarDirections(industry, view === 'sto');
  /**
   * Direction detail modal (DAT-9115, R18). Figma E `4833:15639`; opens when
   * a radar card title is clicked. State lives here (not inside the
   * section) because the section is a sibling to the account-pipeline
   * blocks, and the modal must sit above the whole dashboard so a scroll
   * inside the modal does not fight the dashboard's scroll container.
   *
   * `null` = closed. The hook stays mounted and treats null as idle, so
   * closing + re-opening the same direction keeps the previous response
   * on screen while the fresh fetch settles.
   */
  const [openDirectionId, setOpenDirectionId] = useState<string | null>(null);
  const radarDirection = useRadarDirection(openDirectionId);

  /**
   * Adding / removing a lighthouse account (DAT-9086). Offered only to a caller who may write the
   * flag — UX only: the service's own check stands behind it, and a refusal still arrives as a
   * `denied` failure. Both blocks refetch after a change, because the flag MOVES an account
   * between them.
   */
  const canEditLighthouse = canWriteFlag(AccountFlag.LIGHTHOUSE, ldap_groups);
  const flags = useAccountFlag();
  // `restart`, not `reload`: the write changed which accounts are in each block, so the page
  // tokens already walked no longer describe it (see useAccountPipeline).
  const { restart: restartLighthouse } = lighthouseAccounts;
  const { restart: restartReclaim } = reclaimAccounts;
  const reloadAccountBlocks = useCallback(() => {
    restartLighthouse();
    restartReclaim();
  }, [restartLighthouse, restartReclaim]);
  const lighthouseEdits = useLighthouseEdits(flags.setFlag, reloadAccountBlocks);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LighthouseDeleteTarget | null>(null);

  /**
   * `+ Motion` (DAT-9005): the row its dialog is open for, and which block it is in — the outcome
   * is said above that block. Offered on the same terms as Ignore: a user who may write.
   */
  const motionClaim = useMotionClaim();
  const [motionTarget, setMotionTarget] = useState<{
    block: MotionBlock;
    group: AccountPipelineGroup;
    opp: AccountPipelineOpportunity;
  } | null>(null);
  const claimFrom = (block: MotionBlock) =>
    canWrite
      ? (group: AccountPipelineGroup, opp: AccountPipelineOpportunity) =>
          setMotionTarget({ block, group, opp })
      : undefined;
  // One hook for both blocks: a pair is the same pair whichever block shows it. Handed down only
  // to a user who may write, so nobody else is offered an Ignore that would 403.
  const dismissal = useDismissal();
  const canWrite = hasIvtEditorAccess(ldap_groups);
  /**
   * The STO view's industry scope could not be resolved. With no "all industries" request to
   * fall back on, there is nothing the view can ask for, so it is an error with a retry that
   * re-resolves the list — reported through the same load-state slots as a failed fetch.
   */
  const industriesFailed = view === 'sto' && allowed.status === 'failed';
  /**
   * The STO view's load state, with the industry scope folded in. The hooks hold `loading`
   * up while `industry` is null, which is right while the list loads and wrong once it has
   * failed or come back empty — those two are answers, not waits, so they clear it here.
   */
  const stoState =
    industriesFailed
      ? { loading: false, error: '无法加载可见行业', denial: null, reload: allowed.reload }
      : view === 'sto' && allowed.status === 'ready' && industry === null
        ? { loading: false, error: null, denial: null, reload: stoFlow.reload }
        : stoFlow;
  // The load states are identical in shape for both views, so everything below reads the
  // ACTIVE view's and needs no branch. Only `data` is per-view, because its type is.
  const { loading, error, denial, reload } = view === 'gm' ? gmFlow : stoState;
  const data = view === 'gm' ? gmFlow.data : stoFlow.data;

  /**
   * The block title's `(N Account, N opps, $N)` triple. The design puts it on the LIST title,
   * not on the card above it — the card's own header carries the name alone.
   */
  const blockCounter = (c: { accountCount?: number; tagged?: { oppCount?: number; arr?: number }; untagged?: { oppCount?: number; arr?: number } } | undefined) => {
    if (!c) return undefined;
    const opps = (c.tagged?.oppCount ?? 0) + (c.untagged?.oppCount ?? 0);
    const arr = (c.tagged?.arr ?? 0) + (c.untagged?.arr ?? 0);
    return `(${c.accountCount ?? 0} Account, ${opps} opps, ${formatArr(arr)})`;
  };

  /** Denial first: a 403 sets `error` too, and the denial is the more specific answer. */
  const blockFailure = (f: { denial: DenialKind | null; error: string | null }) =>
    f.denial !== null ? 'denied' : f.error !== null ? 'error' : null;

  const refreshAnchor = () => setAnchor(new Date());

  /**
   * Permitted nothing. A property of the last SUCCESSFUL response only — deliberately
   * independent of `loading` and `error`, both of which were in this expression once and
   * both of which made it lie: either flag being set flipped it to false while the old
   * response was still on screen, so a caller permitted nothing had the permission notice
   * replaced by a zeroed band (during any refetch, and again on a failed one).
   *
   * Reading emptiness as "no permission" is sound because a permitted caller always gets
   * one row per industry in scope, all-zero rows included — see useIndustryFlow.
   *
   * ⚠️ The GM half does NOT generalise to the STO response: that one counts from the assets
   * table outward, so zero rows there is a permitted caller with no UC-linked asset in
   * scope — a real answer, not a permission problem.
   *
   * The STO half reads a different source for the same fact: GetAllowedIndustries answering
   * with an empty set. The STO view queries one permitted industry at a time, so a caller
   * permitted none has nothing to query, and no STO request is sent at all.
   */
  const noPermission =
    (view === 'gm' && gmFlow.data !== null && (gmFlow.data.industries?.length ?? 0) === 0) ||
    (view === 'sto' && permittedIndustries !== null && permittedIndustries.length === 0);

  /**
   * Not allowed to open this dashboard at all — a 403, not an empty scope.
   *
   * ⚠️ A DIFFERENT state from `noPermission` above, and the two must not be merged even
   * though both are "permission". `noPermission` is a 200 saying "you see no industries";
   * this is the service refusing the operation because the GM view is restricted to
   * `ivthub-admin` (DAT-8997). Merging them would tell an admin-less caller to request
   * industry data access, which cannot help them — the exact misdirection the service
   * added the `admin-not-permitted` reason code to prevent.
   *
   * Any denial kind, not just `admin-not-permitted`: an expired token or an unregistered
   * route also make the page unopenable, and DENIAL_HINTS already words each one — which
   * is why it matters that the STO view being ANY_MEMBER does not make it 403-proof.
   *
   * ⚠️ Deliberately does not consult `ldap_groups`, even though the initial `view` above
   * does. The service is the authority on whether a view may be opened; a client-side
   * group check here would be a second one that can disagree with it.
   */
  const noAccess = denial !== null;

  /**
   * A refetch, as opposed to the first load. Worth its own flag because the two need
   * opposite treatments: the first load has nothing to show so it gets the full spinner,
   * while a refetch already has last window's numbers on screen and must NOT blank them.
   *
   * Without any indicator here the page looks frozen — the numbers are the previous
   * window's and nothing says a request is in flight, which reads as "switching the window
   * did nothing" even though the fetch has gone out (pinned in useIndustryFlow.test).
   */
  const refreshing = loading && data !== null;

  /**
   * The content area, the one part that is not shared. Built here rather than branched
   * inside the JSX so each view reads its OWN hook's data and the two output types never
   * have to be widened into a union and cast back.
   *
   * Null exactly when the active view has no response yet, which is what the gate below
   * used to spell `data &&`.
   */
  const content =
    view === 'gm'
      ? gmFlow.data && (
          <SpaceBetween size="l">
            <KpiBand total={gmFlow.data.total} />
            <IndustryTable rows={gmFlow.data.industries ?? []} />
            {/* The two trailing-three-month trend columns land inside the table. */}
          </SpaceBetween>
        )
      : stoFlow.data && (
          /* Three parts, laid out with a 70px gap between them (DAT-9259 item 1): asset list,
             lighthouse pipeline, reclaim pipeline. Each part keeps its OWN size="l" (20px)
             internally so the inner cards still breathe their original amount — only the
             part-to-part separation is widened. The three wrappers are not purely cosmetic:
             without them the outer SpaceBetween spaced every child the same way and there
             was no way to tell "part boundary" from "inner card". */
          <div className="ivt-dashboard__sto-parts">
            <SpaceBetween size="l">
              {/* The band belongs to the asset list — it counts asset-attributed pipeline only,
                  which is why its figures are smaller than the GM view's. */}
              <Box variant="h3">我的资产清单</Box>
              <KpiBand total={stoFlow.data.total} />
              <ColumnLayout columns={2}>
                {/* Not the blocks' `data`: the card ignores the filter, so it must not blank when the
                    filter changes (see useAccountPipeline's `coverage`). */}
                <CoverageCard coverage={lighthouseAccounts.coverage} lighthouse />
                <CoverageCard coverage={reclaimAccounts.coverage} lighthouse={false} />
              </ColumnLayout>
              {/* One row per (asset, industry) pair, so an asset under two industries' use
                  cases appears twice — the rows are not a division of `total`. */}
              <AssetTable rows={stoFlow.data.assets ?? []} />
            </SpaceBetween>
            <SpaceBetween size="l">
              {/* The last lighthouse add / removal, and the last `+ Motion` from this block. */}
              {renderNotices([...lighthouseEdits.notices, ...motionClaim.noticesFor('lighthouse')])}
              <AccountPipelineList
                accounts={lighthouseAccounts.data?.accounts ?? []}
                lighthouse
                onAdd={canEditLighthouse ? () => setAddOpen(true) : undefined}
                onDelete={
                  canEditLighthouse
                    ? (accountId, accountName) => setDeleteTarget({ accountId, accountName })
                    : undefined
                }
                dismissal={canWrite ? dismissal : undefined}
                writesDisabled={lighthouseEdits.busy}
                onClaim={claimFrom('lighthouse')}
                loading={lighthouseAccounts.loading}
                pageIndex={lighthouseAccounts.pageIndex}
                pagesCount={lighthouseAccounts.pagesCount}
                hasNextPage={lighthouseAccounts.hasNextPage}
                onPageChange={lighthouseAccounts.goToPage}
                onNextPage={lighthouseAccounts.nextPage}
                // The title follows the filter; the CoverageCard above does not (it reads `coverage`).
                // `filteredTotals` is optional on the wire: a server without it means no filter.
                counter={blockCounter(lighthouseAccounts.data?.filteredTotals ?? lighthouseAccounts.data?.coverage)}
                minScore={lighthouseScore}
                onMinScoreChange={setLighthouseScore}
                failure={blockFailure(lighthouseAccounts)}
                filter={{ query: lighthouseQuery, onChange: setLighthouseQuery, options: lighthouseOptions }}
              />
            </SpaceBetween>
            <SpaceBetween size="l">
              {renderNotices(motionClaim.noticesFor('reclaim'))}
              <AccountPipelineList
                accounts={reclaimAccounts.data?.accounts ?? []}
                lighthouse={false}
                dismissal={canWrite ? dismissal : undefined}
                onClaim={claimFrom('reclaim')}
                loading={reclaimAccounts.loading}
                pageIndex={reclaimAccounts.pageIndex}
                pagesCount={reclaimAccounts.pagesCount}
                hasNextPage={reclaimAccounts.hasNextPage}
                onPageChange={reclaimAccounts.goToPage}
                onNextPage={reclaimAccounts.nextPage}
                counter={blockCounter(reclaimAccounts.data?.filteredTotals ?? reclaimAccounts.data?.coverage)}
                minScore={reclaimScore}
                onMinScoreChange={setReclaimScore}
                failure={blockFailure(reclaimAccounts)}
                filter={{ query: reclaimQuery, onChange: setReclaimQuery, options: reclaimOptions }}
              />
            </SpaceBetween>
            {/* Radar section LAST (DAT-9115, R17; Xin 2026-10-30: must be the
                final section of the STO view, below the two account-pipeline
                blocks). Owns its own load / error state; the asset and
                account blocks above are independent. R18 wires the detail
                modal through `onOpenDirection`; the preferences gear routes
                to the R16 Admin page.
                No industry carried across: RadarConfigPage picks its own
                default from GetAllowedIndustries, and the page comments make
                that an explicit contract ("An industry picked here is NOT
                persisted to Dashboard's picker") — so passing one here would
                be a new, unused argument. */}
            <RadarSection
              state={radarDirections}
              onOpenDirection={setOpenDirectionId}
              onOpenPreferences={openRadarPreferences}
            />
          </div>
        );

  return (
    <div className="ivt-dashboard">
      {/* Title row — self-drawn rather than the shared PageHeader, whose white bar
          and full-width divider do not match this design (which greets the viewer
          instead of naming the page). Sits INSIDE the page's 32px padding so the
          controls line up with the right edge of the KPI band and table below. */}
      <div className="ivt-dashboard__header">
        <div className="ivt-dashboard__greeting">
        <Box variant="h1" fontSize="display-l" fontWeight="light">
          {/* given_name is the person's name and display_name is the reachable
              fallback — AuthContext already degrades it to the login alias when the
              token has no display_name claim.
              `sub` is kept as a belt-and-braces third link, but it cannot actually
              rescue anything: AuthContext builds display_name as
              `jwt.display_name || jwt.sub`, so display_name is empty only when sub is
              empty too. Do not read its presence here as a case that has been handled. */}
          Welcome, {given_name || display_name || sub}
        </Box>
        </div>
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          {/* In-flight indicator for a refetch, to the LEFT of the selector that caused
              it, in a fixed-width slot.
              The slot is the point: this group is right-aligned, so an element that
              appears and disappears would change the group's width and shift the window
              selector sideways on every switch. Reserving the space costs 84px of empty
              header and keeps every control still. */}
          <div className="ivt-dashboard__refreshing-slot" role="status" aria-live="polite">
            {refreshing && (
              <span className="ivt-dashboard__refreshing">
                <Spinner size="normal" />
                <Box variant="small" color="text-body-secondary">
                  更新中…
                </Box>
              </span>
            )}
          </div>
          {/* Figma sizes the trigger at 180px; Cloudscape's Select otherwise
              shrinks to its label and the window dates get cramped. */}
          <div className="ivt-dashboard__window-select">
            <WindowSelect
              today={anchor}
              selectedId={effectivePresetId}
              onOpen={refreshAnchor}
              onChange={(id) => {
                refreshAnchor();
                setPresetId(id);
              }}
            />
          </div>
          {/* STO only. The GM view renders a per-industry matrix, so narrowing to one
              industry there would leave a one-row table restating the band. */}
          {view === 'sto' && (
            <div className="ivt-dashboard__industry-select">
              <IndustrySelect
                industries={permittedIndustries}
                status={allowed.status}
                selected={industry}
                onChange={setPickedIndustry}
              />
            </div>
          )}
          {/* Both segments pinned to one width in the CSS — the two labels differ a
              lot in length, and Cloudscape sizes each segment to its own text. */}
          <div className="ivt-dashboard__view-switch">
            <SegmentedControl
              selectedId={view}
              // The window is NOT reset with the view, and the anchor is NOT re-read: the
              // switch answers "show me the same window from the other side", so moving
              // the window underneath it would make the two bands incomparable.
              onChange={({ detail }) => {
                const next = detail.selectedId as DashboardView;
                // Close the radar detail modal when the view leaves STO —
                // the radar section is STO-only, so an id left behind
                // would silently re-open the modal (and refetch) when
                // the user flips back to STO. Clear in the handler, not
                // via a derived `view === 'sto' ? id : null` prop, so
                // the state carries the user's actual intent.
                if (next !== 'sto') setOpenDirectionId(null);
                setView(next);
              }}
              label="视图切换"
              options={[
                { id: 'gm', text: 'GM View' },
                { id: 'sto', text: 'STO & IS/ISA View' },
              ]}
            />
          </div>
        </SpaceBetween>
      </div>

      {/* `!noAccess` for the same reason as the error block below, and it became necessary the
          moment a denial started dropping the cached response: `data` is then null, so the
          first-load condition `loading && !data` is satisfied by the request that FOLLOWS a 403
          and the full-page spinner rendered on top of the denial panel. Fixing the review's
          finding is what created that overlap. */}
      {loading && !data && !noAccess && (
        <div className="ivt-dashboard__loading">
          <Spinner size="large" />
          <span>{VIEW_COPY[view].loading}</span>
        </div>
      )}

      {/* ⚠️ NOT an Alert, and that is the point. `type="error"` is red and red reads as "the
          system broke" — but nothing broke here: the service answered correctly and the answer
          is "this page is not yours". A red banner sends the reader to refresh and to report a
          fault, which is the same wrong instinct the copy already refuses to encourage by
          withholding a retry button; the colour had to stop contradicting the words.
          `type="info"` was the cheap alternative and was rejected: it is exactly what the
          industry-scope notice below uses, and this CR spends real effort keeping those two
          states apart — making them look identical would undo that.
          The shape instead copies IvtLayout's non-member panel, which is this package's
          existing house style for "you cannot open this surface" — a heading and prose,
          centred, no banner chrome. See `.ivt-dashboard__denied`.

          The window selector and the view switch above stay mounted on purpose: they show the
          page is alive rather than broken, and a non-admin who reached this panel by choosing
          GM leaves it from right here. Only the content area is replaced. */}
      {noAccess && (
        <div className="ivt-dashboard__denied">
          <h2>无权访问该看板</h2>
          <p>{DENIAL_HINTS[denial]}</p>
        </div>
      )}

      {error && !noAccess && (
        <Alert
          type="error"
          header={VIEW_COPY[view].errorHeader}
          action={
            <Button
              onClick={() => {
                refreshAnchor();
                reload();
              }}
            >
              重试
            </Button>
          }
        >
          {error}
          {/* The fetch hook keeps the previous response on screen rather than blanking the
              page, so after a failed window switch the numbers below belong to the OLD
              window while the dropdown already names the new one. Naming the window they
              came from is what stops them being read as the requested window's.
              Gated on the SAME condition as the content block, not merely on the response
              carrying a window: a caller permitted nothing has nothing rendered below, and
              this line would then point at content that is not there. */}
          {data?.window && !noPermission && (
            <Box variant="small" color="text-status-inactive">
              下方仍是 {data.window.start} → {data.window.end} 的数据
            </Box>
          )}
        </Alert>
      )}

      {noPermission && !noAccess && (
        <Alert type="info" header="无 Industry 权限">
          当前账号没有任何行业的查看权限,因此没有可展示的数据。
        </Alert>
      )}

      {/* `!noAccess` as well: the hook keeps the last successful response on screen through a
          failure, so a caller whose access is revoked mid-session (or who lands here with a
          stale cache) would otherwise keep reading numbers the service has just refused to
          serve them. */}
      {content && !noPermission && !noAccess && (
        <div
          className={refreshing ? 'ivt-dashboard__content is-stale' : 'ivt-dashboard__content'}
          aria-busy={refreshing}
        >
          {content}
        </div>
      )}

      {/* Mounted per opening, so each one starts empty — see AddLighthouseAccountModal. */}
      {/* Mounted per opening, like the lighthouse dialogs. Only with an industry: the STO view
          always has one once its blocks render, and Select is scoped to it. */}
      {motionTarget && industry && (
        <AddMotionModal
          key={motionTarget.opp.opptyId}
          oppName={motionTarget.opp.oppName ?? motionTarget.opp.opptyId ?? ''}
          industry={industry}
          allowedIndustries={permittedIndustries}
          recommendedAssetIds={visibleSuggestions(
            motionTarget.opp,
            (motionTarget.block === 'lighthouse' ? lighthouseScore : reclaimScore) ?? DEFAULT_MIN_SCORE,
          ).map((sg) => sg.assetId)}
          onDismiss={() => setMotionTarget(null)}
          onConfirm={(choice) => {
            const { block, group, opp } = motionTarget;
            setMotionTarget(null);
            void motionClaim.claim(
              {
                block,
                accountId: group.account?.accountId,
                accountName: group.account?.accountName,
                opp,
              },
              choice,
            );
          }}
        />
      )}
      {addOpen && (
        <AddLighthouseAccountModal
          industry={industry}
          onDismiss={() => setAddOpen(false)}
          onConfirm={(targets) => {
            setAddOpen(false);
            void lighthouseEdits.add(targets);
          }}
        />
      )}
      {deleteTarget && (
        <DeleteLighthouseAccountModal
          account={deleteTarget}
          clearFlag={flags.clearFlag}
          onDismiss={() => setDeleteTarget(null)}
          onDeleted={(label) => {
            setDeleteTarget(null);
            lighthouseEdits.announceRemoved(label);
            reloadAccountBlocks();
          }}
        />
      )}
      {/* Radar detail modal (R18). Mounted unconditionally so the hook can
          treat null as idle; the Cloudscape Modal itself keys off `visible`
          and does not render chrome when `directionId` is null. */}
      <RadarDirectionModal
        directionId={openDirectionId}
        state={radarDirection}
        onDismiss={() => setOpenDirectionId(null)}
        onSuccess={() => {
          // R19: an FP or ADOPT write flips the direction out of CANDIDATE,
          // so it drops off the dashboard — close the modal and reload the
          // list. ListRadarDirections is cheap (one call, one industry) and
          // the hook keeps the previous response on screen while the refresh
          // settles, so the card grid does not flash to a loading state.
          setOpenDirectionId(null);
          radarDirections.reload();
        }}
      />
    </div>
  );
}

export default DashboardPage;
